import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import ArticleForm, { Article } from './components/ArticleForm'
import Loading from '../../components/common/Loading'
import ErrorMessage from '../../components/common/ErrorMessage'
import api from '../../services/api'
import './News.css'

function ArticleEditor() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditing = Boolean(id)

  const [article, setArticle] = useState<Article | undefined>()
  const [loading, setLoading] = useState(isEditing)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!id) return

    const numericId = Number(id)
    if (isNaN(numericId)) {
      navigate('/admin/news', { replace: true })
      return
    }

    loadArticle(numericId)
  }, [id, navigate])

  const loadArticle = async (articleId: number) => {
    try {
      setLoading(true)
      setError('')
      const data = await api.get<Article>(`/articles/id/${articleId}`)
      setArticle(data)
    } catch (err) {
      navigate('/admin/news', { replace: true })
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (formData: Partial<Article>) => {
    try {
      setIsSubmitting(true)
      setError('')

      if (isEditing && id) {
        await api.put(`/articles/${id}`, formData)
      } else {
        await api.post('/articles', formData)
      }

      navigate('/admin/news')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate('/admin/news')
  }

  if (loading) {
    return <Loading />
  }

  return (
    <div className="article-editor-page">
      <div className="editor-header">
        <button type="button" className="back-link" onClick={handleCancel}>
          ← Retour aux actualités
        </button>
        <h1>{isEditing ? 'Modifier l\'article' : 'Nouvel article'}</h1>
      </div>

      {error && <ErrorMessage message={error} />}

      <ArticleForm
        article={article}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={isSubmitting}
      />
    </div>
  )
}

export default ArticleEditor
